import { motion } from "framer-motion";
import { Shield, TrendingUp, Users, Award, Headphones, BookOpen, Zap, Globe } from "lucide-react";
import {useNavigate} from "react-router-dom";

export default function Features() {
  const navigate = useNavigate();
  const agentFeatures = [
    {
      id: 1,
      title: "High Commission Rates",
      subtitle: "Earn up to 25% commission on every policy sold with our competitive structure",
      color: "from-emerald-500 to-emerald-600",
      tint: "bg-emerald-500",
      icon: TrendingUp,
    },
    {
      id: 2,
      title: "Comprehensive Training",
      subtitle: "Access to industry-leading training programs and certification courses",
      color: "from-blue-500 to-blue-600",
      tint: "bg-blue-500",
      icon: BookOpen,
    },
    {
      id: 3,
      title: "24/7 Support Team",
      subtitle: "Round-the-clock assistance for all your questions and technical needs",
      color: "from-purple-500 to-purple-600",
      tint: "bg-purple-500",
      icon: Headphones,
    },
    {
      id: 4,
      title: "Advanced Tools",
      subtitle: "State-of-the-art dashboard with real-time analytics and reporting",
      color: "from-orange-500 to-orange-600",
      tint: "bg-orange-500",
      icon: Zap,
    },
    {
      id: 5,
      title: "Global Network",
      subtitle: "Join our worldwide community of successful insurance professionals",
      color: "from-teal-500 to-teal-600",
      tint: "bg-teal-500",
      icon: Globe,
    },
    {
      id: 6,
      title: "Performance Rewards",
      subtitle: "Earn bonuses, recognition, and exclusive perks for outstanding performance",
      color: "from-indigo-500 to-indigo-600",
      tint: "bg-indigo-500",
      icon: Award,
    },
  ];

  return (
    <section className="px-4 py-20 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-16 text-center"
        >
          <h2 className="text-3xl font-bold text-gray-900 sm:text-4xl lg:text-5xl">
            Why Choose TripSecure?
          </h2>
          <p className="mx-auto mt-4 max-w-3xl text-lg text-gray-600">
            Join thousands of successful agents who have transformed their careers with our comprehensive platform
          </p>
        </motion.div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {agentFeatures.map((feature, index) => (
            <motion.div
              key={feature.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1, duration: 0.5 }}
              whileHover={{ y: -8, scale: 1.02 }}
              className="overflow-hidden relative p-6 bg-white rounded-2xl ring-1 ring-gray-200 shadow-lg transition-all duration-300 group hover:shadow-xl"
            >
              {/* Background Gradient */}
              <div className={`absolute inset-0 bg-gradient-to-br ${feature.color} opacity-0 transition-opacity duration-300 group-hover:opacity-5`} />
              
              {/* Icon */}
              <div className={`relative mb-4 flex h-14 w-14 items-center justify-center rounded-xl ${feature.tint} text-white shadow-lg transition-transform duration-300 group-hover:scale-110`}>
                <feature.icon size={28} />
              </div>

              {/* Content */}
              <div className="relative">
                <h3 className="mb-2 text-xl font-semibold text-gray-900 group-hover:text-gray-800">
                  {feature.title}
                </h3>
                <p className="leading-relaxed text-gray-600">
                  {feature.subtitle}
                </p>
              </div>

              {/* Hover Effect */}
              <div className="absolute inset-0 rounded-2xl ring-2 ring-transparent transition-all duration-300 group-hover:ring-emerald-200" />
            </motion.div>
          ))}
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-16 text-center"
        >
          <div className="inline-flex items-center px-6 py-3 text-emerald-700 bg-emerald-50 rounded-full">
            <Shield size={20} className="mr-2" />
            <span className="font-medium">Ready to start your journey?</span>
          </div>
          <div className="mt-4">
            <button onClick={()=>navigate("/signin")} className="px-8 py-3 font-semibold text-white bg-emerald-600 rounded-full transition-all duration-300 hover:bg-emerald-700 hover:scale-105">
              Get Started Today
            </button>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
